import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { HeaderTitleContextProvider } from '../../../contexts/HeaderTitle';
import MultiSelect from '../../atoms/Fields/MultiSelect';
import Logo from '../../atoms/Logo';
import Header from '../../organisms/Header';
import SideMenu from '../../molecules/SideMenu';
import {
  StyledCollapseToggle,
  StyledFooterLogoMini,
  StyledLayoutBaseContent,
  StyledLayoutBaseDropdown,
  StyledLayoutBaseMain,
  StyledLayoutBaseSidebar,
  StyledLayoutBaseWrapper,
  StyledPoweredByLabel,
  StyledSidebarFooter,
  StyledSidebarIdentity,
  StyledSidebarIdentityName,
  StyledSidebarSpacer,
  StyledSwitcherBox,
} from './styles';
import Avatar from '../../atoms/Avatar';
import { AvatarSize } from '../../atoms/Avatar/enums';
import {
  useClientsDropdownItems,
  useClientSelectValue,
  useOnClientSelectChange,
  useOnLogoutClick,
  useRefetchQueriesOnClientChange,
} from './hooks';
import useUserContext from '../../../hooks/useUserContext';
import { LayoutBaseProps } from './types';
import usePathMatchesRoute from '../../../hooks/usePathMatchesRoute';
import routes from '../../../setup/consts/routes';
import { useAuthTokens } from '../../../contexts/User/hooks';
import IconButtonLink from '../../atoms/IconButton/iconButtonLink';
import {
  IconChevronLeft,
  IconStats,
  IconWarehouse2,
} from '../../../assets/svg/icons';
import { StyledBox } from '../../styles/Grid/styles';
import Spinner from '../../atoms/Spinner';
import { SpinnerMode } from '../../atoms/Spinner/enums';
import renderRadioItemWithCheckboxes from '../../atoms/Fields/Radio/content/withCheckboxes';

const COLLAPSE_KEY = 'sdg.sidebar.collapsed';

const LayoutBase: React.FC<LayoutBaseProps> = ({
  children,
  items,
  settingsItems,
  ...props
}) => {
  const intl = useIntl();
  const onLogout = useOnLogoutClick();
  const { user } = useUserContext();
  const {
    items: clientSelectItems,
    fetchMore,
    hasNextPage,
  } = useClientsDropdownItems();
  const onClientSelectChange = useOnClientSelectChange();
  const value = useClientSelectValue();
  const isSettingsSection = usePathMatchesRoute(
    routes.SETTINGS_SECTION,
    undefined,
    false
  );
  const isWarehousePage = usePathMatchesRoute(routes.WAREHOUSE, undefined);
  const isReportPage = usePathMatchesRoute(routes.REPORT, undefined);
  const authTokens = useAuthTokens();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    }
  }, [collapsed]);

  useRefetchQueriesOnClientChange(value?.value, value?.checkedSubItems);

  const selectedClient = useMemo(
    () =>
      clientSelectItems.find(
        ({ value: clientId }) => clientId === value?.value
      ),
    [value?.value, clientSelectItems]
  );

  const renderSelectedItems = useCallback(
    (selected) =>
      selectedClient
        ? renderRadioItemWithCheckboxes(selected)({
            ...selectedClient,
            checked: true,
          })
        : null,
    [selectedClient]
  );

  const SwitcherComponent = (isReportPage || isWarehousePage) && (
    <StyledSwitcherBox>
      <IconButtonLink
        isActive={isWarehousePage}
        isSecondary
        icon={IconWarehouse2}
        to={routes.WAREHOUSE}
      />
      <IconButtonLink
        isActive={isReportPage}
        isSecondary
        icon={IconStats}
        to={routes.REPORT}
      />
    </StyledSwitcherBox>
  );

  return (
    <HeaderTitleContextProvider>
      <StyledLayoutBaseWrapper>
        <StyledLayoutBaseSidebar collapsed={collapsed}>
          <StyledCollapseToggle
            type='button'
            onClick={() => setCollapsed((c) => !c)}
            collapsed={collapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <IconChevronLeft />
          </StyledCollapseToggle>
          {user && (
            <StyledSidebarIdentity
              to={routes.ORDERS}
              collapsed={collapsed}
              title={
                (typeof selectedClient?.label === 'string'
                  ? selectedClient?.label
                  : undefined) || user?.name
              }
            >
              <Avatar
                size={AvatarSize.Small}
                src={user.accountDetails.logo ?? undefined}
                alt={user.name}
              />
              {!collapsed && (
                <StyledSidebarIdentityName>
                  {(typeof selectedClient?.label === 'string'
                    ? selectedClient?.label
                    : undefined) || user.name}
                </StyledSidebarIdentityName>
              )}
            </StyledSidebarIdentity>
          )}
          {(!isSettingsSection || authTokens?.sdgAuthToken) && (
            <StyledLayoutBaseDropdown collapsed={collapsed}>
              <MultiSelect
                placeholder='Select channel'
                onChange={onClientSelectChange}
                items={clientSelectItems}
                infiniteScrollProps={{
                  dataLength: clientSelectItems.length,
                  hasMore: hasNextPage,
                  loader: (
                    <StyledBox
                      justifyContent='center'
                      alignItems='center'
                      p='2rem'
                    >
                      <Spinner mode={SpinnerMode.Dark} />
                    </StyledBox>
                  ),
                  next: fetchMore,
                  scrollableTarget: 'clients',
                }}
                {...{ value, renderSelectedItems }}
              />
            </StyledLayoutBaseDropdown>
          )}
          <SideMenu {...{ items, ...props }} collapsed={collapsed} />
          <StyledSidebarSpacer />
          <StyledSidebarFooter collapsed={collapsed}>
            {!collapsed && <StyledPoweredByLabel>Powered by</StyledPoweredByLabel>}
            <StyledFooterLogoMini collapsed={collapsed}>
              <Logo to={routes.ORDERS} />
            </StyledFooterLogoMini>
          </StyledSidebarFooter>
        </StyledLayoutBaseSidebar>
        <StyledLayoutBaseMain id='main-content'>
          <Header
            menu={{
              items: settingsItems,
              title: intl.formatMessage({
                id: 'sidebar.settings.title',
                defaultMessage: 'Settings',
              }),
              onLogout,
            }}
            centerComponent={SwitcherComponent}
          />
          <StyledLayoutBaseContent>{children}</StyledLayoutBaseContent>
        </StyledLayoutBaseMain>
      </StyledLayoutBaseWrapper>
    </HeaderTitleContextProvider>
  );
};

export default LayoutBase;
